/**
 * Order & Product API
 */
import type { ItemResponse, ListResponse, DeleteResponse } from '../../types/api.js';
import type { Order, Product, ProductCategory } from '../../types/models.js';
import { apiRequest } from './core.js';

/** Server shape of GET /orders/summary (workspace-wide, filter-aware). */
export interface OrdersSummary {
  orderCount: number;
  /** Paid, not cancelled, net of refunds — over every matching order. */
  totalRevenue: number;
  totalCogs: number;
  totalProfit: number;
  avgMargin: number;
  lossOrders: number;
  itemsMissingCost: number;
}

export const ordersApi = {
  list: (params?: { search?: string; status?: string; paymentStatus?: string; fulfillmentStatus?: string; customerId?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    const searchParams = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined) searchParams.set(k, String(v)); });
    return apiRequest<ListResponse<Order>>(`/orders?${searchParams}`);
  },
  /**
   * WORKSPACE-WIDE order economics. The Orders page's headline tiles must not
   * be summed from the loaded page — that understated a 280-order workspace by
   * more than half (audit 2026-08-05, D5).
   */
  summary: (params?: { search?: string; status?: string; paymentStatus?: string; fulfillmentStatus?: string; customerId?: string; segment?: string }) => {
    const searchParams = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined) searchParams.set(k, String(v)); });
    return apiRequest<ItemResponse<OrdersSummary>>(`/orders/summary?${searchParams}`);
  },
  get: (id: string) => apiRequest<ItemResponse<Order>>(`/orders/${id}`),
  create: (data: Partial<Order>) => apiRequest<ItemResponse<Order>>('/orders', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Order>) => apiRequest<ItemResponse<Order>>(`/orders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStatus: (id: string, data: { status?: string; paymentStatus?: string; fulfillmentStatus?: string }) =>
    apiRequest<ItemResponse<Order>>(`/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<DeleteResponse>(`/orders/${id}`, { method: 'DELETE' })
};

export const productsApi = {
  list: (params?: { search?: string; status?: string; category?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc'; withAttributes?: 'true' }) => {
    const searchParams = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined) searchParams.set(k, String(v)); });
    return apiRequest<ListResponse<Product>>(`/products?${searchParams}`);
  },
  get: (id: string) => apiRequest<ItemResponse<Product>>(`/products/${id}`),
  create: (data: Partial<Product>) => apiRequest<ItemResponse<Product>>('/products', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Product>) => apiRequest<ItemResponse<Product>>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<DeleteResponse>(`/products/${id}`, { method: 'DELETE' }),
  updateStock: (id: string, data: { stock?: number; adjustment?: number }) =>
    apiRequest<ItemResponse<Product>>(`/products/${id}/stock`, { method: 'PATCH', body: JSON.stringify(data) }),
  getCategories: () => apiRequest<ListResponse<ProductCategory>>('/products/meta/categories')
};
